/**
 * Отметка декларации из файла декларации (sout-results-declaration-and-statement-date) — командная
 * карточка заказа, блок «Результат СОУТ по местам», рядом с «Загрузить сводную ведомость». Декларацию
 * соответствия условий труда формирует «Аттестация 2.0» (.docx) — не во всех заказах; загружает её
 * команда, клиенту кнопки нет.
 *
 * Поток: «Загрузить декларацию» → выбор .docx → сервер разбирает места и реквизиты → окно сверки: место
 * декларации ищется в таблице результатов по «N раб.места» и должности (решение заказчика) → «Отметить
 * декларацию» отдаёт места наверх (`onApply`). Сверка — на форме: номера мест после подстановки
 * сводной ведомости есть только в её несохранённых полях. Сервер ничего не сохраняет — признак
 * «Декларация» сохраняется кнопкой «Сохранить результаты» по действующим правилам (только класс 1–2).
 *
 * Отметки только добавляются: у мест, которых нет в декларации, «Декларация» не снимается. Место, где
 * совпал номер, но не должность, по умолчанию не отмечено («проверьте»); место с классом не 1–2
 * отметить нельзя. ИНН декларации, не совпавший с организацией заказа, — предупреждение, не запрет.
 */
import { useRef, useState, type ChangeEvent } from 'react'
import { Upload } from 'lucide-react'
import { ApiError, teamApi, type SoutDeclaration, type SoutLine } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import {
  SOUT_DECLARATION_APPLY_LABEL,
  SOUT_DECLARATION_DIALOG_HINT,
  SOUT_DECLARATION_DIALOG_TITLE,
  SOUT_DECLARATION_FORMAT_ERROR,
  SOUT_DECLARATION_INN_MISMATCH,
  SOUT_DECLARATION_PARSE_ERROR,
  SOUT_DECLARATION_STATUS_LABELS,
  SOUT_DECLARATION_UPLOAD_LABEL,
  SOUT_STATEMENT_CANCEL_LABEL,
  SOUT_STATEMENT_UPLOADING_LABEL,
  soutDeclarationApplyLineIds,
  soutDeclarationInitialSelection,
  soutDeclarationMatches,
  type SoutDeclarationMatch,
  type SoutDeclarationTableRow,
} from '@/lib/sout'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { Card } from '@/design-system/components/layout/card'
import { ScrollArea } from '@/design-system/components/layout/scroll-area'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { Checkbox } from '@/design-system/components/primitives/checkbox'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'

export interface SoutDeclarationImportProps {
  orderId: string
  /** Места заказа в порядке анкеты (нумерация «№i» — как в таблице результатов). */
  lines: SoutLine[]
  /** Текущие значения таблицы для сверки: номер, должности, класс — по `lineId`. */
  rows: SoutDeclarationTableRow[]
  /** Поставить «Декларацию» у мест таблицы. */
  onApply: (lineIds: string[]) => void
}

function dash(value: string): string {
  return value.trim() ? value : '—'
}

export function SoutDeclarationImport({
  orderId,
  lines,
  rows,
  onApply,
}: SoutDeclarationImportProps) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [declaration, setDeclaration] = useState<SoutDeclaration | null>(null)

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    // Сброс значения — чтобы повторный выбор того же файла снова вызвал разбор.
    e.target.value = ''
    if (!file) return
    setError(null)
    if (!file.name.toLowerCase().endsWith('.docx')) {
      setError(SOUT_DECLARATION_FORMAT_ERROR)
      return
    }
    setUploading(true)
    try {
      setDeclaration(await teamApi.sout.parseDeclaration(orderId, file))
    } catch (err) {
      if (err instanceof ApiError && err.details && err.details.length > 0) {
        setError(err.details[0].message)
      } else {
        setError(getErrorMessage(err, SOUT_DECLARATION_PARSE_ERROR))
      }
    } finally {
      setUploading(false)
    }
  }

  return (
    <div>
      <Button
        variant="secondary"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        leftIcon={<Upload className="size-4 stroke-current" aria-hidden />}
      >
        {uploading ? SOUT_STATEMENT_UPLOADING_LABEL : SOUT_DECLARATION_UPLOAD_LABEL}
      </Button>
      <input
        ref={fileRef}
        type="file"
        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        aria-label={SOUT_DECLARATION_UPLOAD_LABEL}
        onChange={(e) => void onFile(e)}
      />
      {error ? (
        <Alert variant="danger" className="mt-3">
          {error}
        </Alert>
      ) : null}
      {declaration ? (
        <DeclarationDialog
          declaration={declaration}
          lines={lines}
          rows={rows}
          onApply={(lineIds) => {
            onApply(lineIds)
            setDeclaration(null)
          }}
          onClose={() => setDeclaration(null)}
        />
      ) : null}
    </div>
  )
}

function DeclarationDialog({
  declaration,
  lines,
  rows,
  onApply,
  onClose,
}: {
  declaration: SoutDeclaration
  lines: SoutLine[]
  rows: SoutDeclarationTableRow[]
  onApply: (lineIds: string[]) => void
  onClose: () => void
}) {
  // Сверка фиксируется на момент открытия окна: таблица под окном не меняется.
  const [matches] = useState<SoutDeclarationMatch[]>(() =>
    soutDeclarationMatches(declaration.workplaces, rows),
  )
  const [selected, setSelected] = useState<Set<number>>(() =>
    soutDeclarationInitialSelection(matches),
  )

  const labelByLine = new Map(
    lines.map((line, i) => [
      line.id,
      [`№${i + 1}`, line.conditions.position || '—', line.person.fullName]
        .filter(Boolean)
        .join(' · '),
    ]),
  )
  const classByLine = new Map(rows.map((r) => [r.lineId, r.hazardClass]))
  const lineIds = soutDeclarationApplyLineIds(matches, selected)
  const meta = [
    declaration.organizationName ? `Организация: ${declaration.organizationName}` : '',
    declaration.inn ? `ИНН: ${declaration.inn}` : '',
  ].filter(Boolean)

  function toggle(index: number, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (checked) next.add(index)
      else next.delete(index)
      return next
    })
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent title={SOUT_DECLARATION_DIALOG_TITLE} className="max-w-5xl">
        <Card
          scrollable
          className="max-h-dialog"
          header={SOUT_DECLARATION_DIALOG_TITLE}
          footer={
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-txt-secondary">
                Мест в декларации: {declaration.workplaces.length} · будет отмечено:{' '}
                {lineIds.length}
              </span>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={onClose}>
                  {SOUT_STATEMENT_CANCEL_LABEL}
                </Button>
                <Button onClick={() => onApply(lineIds)} disabled={lineIds.length === 0}>
                  {SOUT_DECLARATION_APPLY_LABEL}
                </Button>
              </div>
            </div>
          }
        >
          <p className="mb-3 text-txt-secondary">{SOUT_DECLARATION_DIALOG_HINT}</p>
          {meta.length > 0 ? <p className="mb-1 text-13 text-txt">{meta.join(' · ')}</p> : null}
          {declaration.basis ? (
            <p className="mb-3 text-13 text-txt-secondary">Основание: {declaration.basis}</p>
          ) : null}
          {declaration.innMatches === false ? (
            <Alert variant="danger" title="Проверьте декларацию" className="mb-3">
              {SOUT_DECLARATION_INN_MISMATCH}
            </Alert>
          ) : null}

          <ScrollArea orientation="horizontal">
            <DataTable>
              <DataTableHead>
                <tr>
                  <DataTableHeaderCell>Отметить</DataTableHeaderCell>
                  <DataTableHeaderCell className="whitespace-nowrap">
                    N раб.места
                  </DataTableHeaderCell>
                  <DataTableHeaderCell>Должность в декларации</DataTableHeaderCell>
                  <DataTableHeaderCell>Рабочее место заказа</DataTableHeaderCell>
                </tr>
              </DataTableHead>
              <DataTableBody>
                {matches.map((m) => {
                  const hazardClass = m.lineId ? (classByLine.get(m.lineId) ?? '') : ''
                  const selectable = Boolean(m.lineId) && m.classAllowed
                  return (
                    <DataTableRow key={m.workplace.index}>
                      <DataTableCell className="align-top">
                        <Checkbox
                          checked={selectable && selected.has(m.workplace.index)}
                          disabled={!selectable}
                          aria-label={`Отметить декларацию для места ${m.workplace.rmNumber}`}
                          onCheckedChange={(v) => toggle(m.workplace.index, v === true)}
                        />
                      </DataTableCell>
                      <DataTableCell className="whitespace-nowrap align-top font-medium text-txt">
                        {dash(m.workplace.rmNumber)}
                      </DataTableCell>
                      <DataTableCell className="align-top">
                        <div className="text-txt">{dash(m.workplace.position)}</div>
                        {m.workplace.headcount > 0 ? (
                          <div className="text-12 text-txt-tertiary">
                            {m.workplace.headcount} чел.
                          </div>
                        ) : null}
                      </DataTableCell>
                      <DataTableCell className="align-top">
                        {m.lineId ? (
                          <div className="text-txt">{labelByLine.get(m.lineId)}</div>
                        ) : null}
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          <Badge
                            variant={
                              m.status === 'matched'
                                ? 'green'
                                : m.status === 'position_mismatch'
                                  ? 'amber'
                                  : 'gray'
                            }
                          >
                            {SOUT_DECLARATION_STATUS_LABELS[m.status]}
                          </Badge>
                          {m.lineId && !m.classAllowed ? (
                            <Badge variant="red">
                              {hazardClass
                                ? `класс ${hazardClass} — декларация невозможна`
                                : 'класс УТ не выбран'}
                            </Badge>
                          ) : null}
                        </div>
                      </DataTableCell>
                    </DataTableRow>
                  )
                })}
              </DataTableBody>
            </DataTable>
          </ScrollArea>
          {matches.some((m) => m.status === 'not_found') ? (
            <p className="mt-3 text-13 text-txt-secondary">
              Места «нет в таблице» ищутся по «N раб.места» — сначала загрузите сводную ведомость
              или заполните номера в таблице.
            </p>
          ) : null}
        </Card>
      </DialogContent>
    </Dialog>
  )
}
